/**
 * The single rule for which free gifts the cart has actually earned.
 *
 * Two freebies exist and they used to be governed separately: the travel pouch
 * had a threshold in ct-atc-flow.js, while the free minis had no removal logic
 * at all — nothing ever pulled them, so deleting the product that earned them
 * left a cart of ₹0 items that could still be checked out for the shipping fee.
 *
 * Both now answer to the same question, asked of the *paid* subtotal (freebie
 * lines are ₹0 and never count towards earning each other):
 *
 *   paid < miniThreshold  → the minis are forfeit
 *   paid < pouchThreshold → the pouch is forfeit
 *
 * Removing the only paid line drops the subtotal to ₹0, so both fire and the
 * cart empties in one step.
 *
 * Two readings of the cart are offered because two callers need different
 * things. forfeitedKeysFrom() reads a /cart.js payload and is the safety net
 * for mutations that never touch the drawer. forfeitedKeysAfterRemoving()
 * reads the rendered lines, so cart.js can fold the forfeited removals into
 * the very request that removes the line the shopper clicked — one round trip,
 * no intermediate cart holding unearned gifts.
 */
(function () {
  'use strict';

  var config = readConfig();

  function readConfig() {
    var el = document.getElementById('ct-freebie-config');
    var parsed = {};

    if (el) {
      try {
        parsed = JSON.parse(el.textContent) || {};
      } catch (e) {
        parsed = {};
      }
    }

    return {
      // The pouch threshold mirrors ct-atc-flow.js, which still owns adding it.
      miniThreshold: parsed.miniThreshold || 49900,
      pouchThreshold: parsed.pouchThreshold || 99900,
      pouchVariantId: String(parsed.pouchVariantId || '47243158683970'),
      miniVariantIds: (parsed.miniVariantIds || []).map(String),
      maxMinis: parsed.maxMinis || 2,
      maxPerMini: parsed.maxPerMini || 1,
      maxPouch: parsed.maxPouch || 1,
      messages: parsed.messages || {},
    };
  }

  function isMini(variantId) {
    return config.miniVariantIds.indexOf(String(variantId)) !== -1;
  }

  function isPouch(variantId) {
    return String(variantId) === config.pouchVariantId;
  }

  function isFreebie(variantId) {
    return isMini(variantId) || isPouch(variantId);
  }

  // Being a gift *product* is not enough to forfeit a line. The samplers are real
  // ₹75 products with their own PDPs, so one can be bought outright — and a paid
  // sampler must count towards the subtotal and never be deleted. Only a line the
  // shopper is actually paying nothing for is a gift.
  function isGiftLine(variantId, linePrice) {
    return isFreebie(variantId) && linePrice === 0;
  }

  // Which of the given freebie lines the paid subtotal no longer covers.
  function forfeit(lines, paid) {
    var keys = [];

    lines.forEach(function (line) {
      if (isMini(line.variantId) && paid < config.miniThreshold) keys.push(line.key);
      if (isPouch(line.variantId) && paid < config.pouchThreshold) keys.push(line.key);
    });

    return keys;
  }

  /**
   * Safety net, from server truth. Pass a parsed /cart.js payload; returns the
   * line item keys that are no longer earned.
   */
  function forfeitedKeysFrom(cart) {
    var items = (cart && cart.items) || [];
    var paid = 0;
    var lines = [];

    items.forEach(function (item) {
      if (isGiftLine(item.variant_id, item.final_line_price)) {
        lines.push({ key: item.key, variantId: item.variant_id });
      } else {
        paid += item.final_line_price;
      }
    });

    return forfeit(lines, paid);
  }

  /**
   * Pre-flight, from the rendered cart. `root` is the cart-items /
   * cart-drawer-items element; `line` is the 1-based index being set to zero.
   * Returns the line item keys that removing it would strand.
   */
  function forfeitedKeysAfterRemoving(root, line) {
    var paid = 0;
    var lines = [];

    Array.prototype.forEach.call(root.querySelectorAll('.cart-item'), function (el) {
      if (String(el.dataset.lineIndex) === String(line)) return;

      var variantId = el.dataset.varId;
      var key = el.dataset.key;
      if (!key) return;

      var price = parseInt(el.dataset.linePrice, 10) || 0;

      if (isGiftLine(variantId, price)) {
        lines.push({ key: key, variantId: variantId });
      } else {
        paid += price;
      }
    });

    return forfeit(lines, paid);
  }

  /**
   * Final gate, run against a fresh /cart.js on checkout click. On top of the
   * threshold forfeits it enforces the quantity caps — at most `maxMinis` gift
   * minis across all mini variants, `maxPerMini` of any one mini variant, and
   * `maxPouch` pouch — trimming the excess.
   *
   * Returns `updates` for /cart/update.js (empty when the cart is clean) plus
   * which rule fired, so the caller can tell the shopper why.
   */
  function checkoutCorrections(cart) {
    var items = (cart && cart.items) || [];
    var paid = 0;
    var gifts = [];

    items.forEach(function (item) {
      if (isGiftLine(item.variant_id, item.final_line_price)) {
        gifts.push(item);
      } else {
        paid += item.final_line_price;
      }
    });

    var updates = {};
    var minisForfeit = false;
    var pouchForfeit = false;
    var capped = false;
    var minisLeft = config.maxMinis;
    var pouchLeft = config.maxPouch;
    // Per mini variant, since one variant can sit on more than one line.
    var perMiniUsed = {};

    gifts.forEach(function (item) {
      var mini = isMini(item.variant_id);

      if (mini && paid < config.miniThreshold) {
        updates[item.key] = 0;
        minisForfeit = true;
        return;
      }
      if (!mini && paid < config.pouchThreshold) {
        updates[item.key] = 0;
        pouchForfeit = true;
        return;
      }

      var used = perMiniUsed[item.variant_id] || 0;
      var allowed = mini ? Math.min(minisLeft, config.maxPerMini - used) : pouchLeft;
      var keep = Math.min(item.quantity, allowed);
      if (keep < item.quantity) {
        updates[item.key] = keep;
        capped = true;
      }
      if (mini) {
        minisLeft -= keep;
        perMiniUsed[item.variant_id] = used + keep;
      } else {
        pouchLeft -= keep;
      }
    });

    return {
      updates: updates,
      changed: Object.keys(updates).length > 0,
      paid: paid,
      minisForfeit: minisForfeit,
      pouchForfeit: pouchForfeit,
      capped: capped,
      miniThreshold: config.miniThreshold,
      pouchThreshold: config.pouchThreshold,
    };
  }

  /**
   * Cap-only pass over a /cart.js payload: how many gift minis are over the
   * allowance, ignoring the thresholds. Returns `updates` for /cart/update.js.
   *
   * checkoutCorrections() already does this, but it also owns the pouch and the
   * thresholds, which ct-atc-flow.js reconciles separately. Splitting the cap out
   * lets the post-mutation safety net trim an over-claim without fighting the
   * pouch bookkeeping.
   *
   * Needed because the Claim buttons are gated by the *rendered* drawer: the
   * picker disappears once two minis are in the cart, but a shopper clicking
   * several Claim buttons before that re-render lands fires several
   * /cart/add.js calls against the same stale state.
   */
  function cappedMiniUpdates(cart) {
    var items = (cart && cart.items) || [];
    var minisLeft = config.maxMinis;
    // Per mini variant, since one variant can sit on more than one line.
    var perMiniUsed = {};
    var updates = {};

    items.forEach(function (item) {
      if (!isGiftLine(item.variant_id, item.final_line_price)) return;
      if (!isMini(item.variant_id)) return;

      var used = perMiniUsed[item.variant_id] || 0;
      var keep = Math.min(item.quantity, Math.max(0, Math.min(minisLeft, config.maxPerMini - used)));
      if (keep < item.quantity) updates[item.key] = keep;
      minisLeft -= keep;
      perMiniUsed[item.variant_id] = used + keep;
    });

    return updates;
  }

  window.CartFreebies = {
    isFreebie: isFreebie,
    isPouch: isPouch,
    forfeitedKeysFrom: forfeitedKeysFrom,
    cappedMiniUpdates: cappedMiniUpdates,
    forfeitedKeysAfterRemoving: forfeitedKeysAfterRemoving,
    checkoutCorrections: checkoutCorrections,
    messages: config.messages,
  };
})();
